// The Admin page: who may use the Barrier Tracker, who may change Hot, who may
// give access, and who sees the top menu.
//
// ONE TABLE OF THE ROSTER (#40), in place of the three tabs #37 had. Everyone
// on `app-users` has a row: people who signed in, people an Admin added, and
// the barrier owners the migration linked, who have never needed to sign in.
// Each row shows the person and five boxes -- Access, Admin, Allow Hot, Can
// Allow Access and Job -- and one Save sends every box changed.
//
// ONE SEARCH, TWO WAYS. What is typed filters the table by name or email, and
// is also sent to the directory, the same search the people picker makes. The
// people it finds who are not on the roster are listed under the table with an
// Add button, which puts them on the roster with no access; they then appear
// in the table to be ticked like anybody else.
//
// ADMIN IS SHOWN, NOT CHANGED. It is membership of the admins security group,
// read live from Entra through `GET /api/directory/admins`
// (entsys-barriertracker-api#34), and changed in the directory. When the API
// cannot read the group the column is blank rather than unticked, and the
// page says why: a column of clear boxes would tell an Admin that a colleague
// is not one, when the truth is that nobody could tell.
//
// AN ACCESS MANAGER SEES THE PAGE TOO (entsys-barriertracker-api#33). They can
// tick Access and Add people, and nothing else: Allow Hot, Can Allow Access and
// Job are shown to them disabled, and so is Access on an Access Manager's row,
// their own included, because the API refuses that change.
//
// JOB SHOWS A PERSON THE TOP MENU (#43): Interplant Transfers, Daily
// Production, Orders and Tables. Admins see it by the role and need nothing
// ticked.
//
// FOUR ATTRIBUTES, NAMED. The page offers `access`, `can_set_hot`,
// `can_manage_access` and `can_use_job_menu` and nothing else on the row. A
// field added to `app-users` later is not shown until somebody decides it
// should be.
//
// NO ACCESS, NO FLAGS. Clearing a person's access clears all three flags in the
// same save, and none can be ticked for somebody without access
// (entsys-barriertracker-api#31, #33 and #43 hold the API to the same rule).

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import * as api from '../api';
import { MIN_SEARCH, SEARCH_DELAY_MS } from '../crud/PeoplePicker';
import { useAsync, type Async } from '../data';
import { ErrorNote, Screen } from '../shell/Screen';
import type { DirectoryPerson, Me, Row } from '../types';

const SLUG = 'app-users';
const ACCESS = 'access';
const CAN_SET_HOT = 'can_set_hot';
const CAN_MANAGE_ACCESS = 'can_manage_access';
const CAN_USE_JOB_MENU = 'can_use_job_menu';
const ACCESS_USER = 'user';
const ACCESS_NONE = 'none';

/** What the page edits, in the order a save sends it. */
const ATTRIBUTES = [ACCESS, CAN_SET_HOT, CAN_MANAGE_ACCESS, CAN_USE_JOB_MENU] as const;
type Attribute = (typeof ATTRIBUTES)[number];
type Values = Record<Attribute, string | boolean>;

/** The route's guard: an Admin, or an Access Manager. The rail hides the link
 *  from anyone else; this turns away somebody who typed the address. The API
 *  refuses their writes regardless. */
export function AdminOnly({ me, children }: { me: Async<Me>; children: ReactNode }) {
  if (me.data?.is_admin || me.data?.can_manage_access) return <>{children}</>;
  if (me.loading && !me.error) return <p className="muted">Loading…</p>;
  return <Navigate to="/" replace />;
}

const id = (row: Row) => Number(row.app_user_id);
const name = (row: Row) => String(row.display_name ?? row.user_principal_name ?? '');
const objectId = (value: unknown) => String(value ?? '').toLowerCase();

function stored(row: Row): Values {
  return {
    [ACCESS]: String(row[ACCESS] ?? ACCESS_NONE),
    [CAN_SET_HOT]: Boolean(row[CAN_SET_HOT]),
    [CAN_MANAGE_ACCESS]: Boolean(row[CAN_MANAGE_ACCESS]),
    [CAN_USE_JOB_MENU]: Boolean(row[CAN_USE_JOB_MENU]),
  };
}

/** `admin` is whether the signed-in person is an Admin; anybody else here is an
 *  Access Manager. */
export function AdminScreen({ admin }: { admin: boolean }) {
  const rows = useAsync(() => api.allRows(SLUG, { order: 'display_name' }), []);
  const admins = useAsync(
    () => api.directoryAdmins().then((ids) => new Set(ids.map(objectId))),
    []
  );
  const [search, setSearch] = useState('');
  // What has been ticked or cleared, by row id, as the whole of the row's four
  // attributes. Kept apart from the rows so a reload does not lose it; a row no
  // longer in the table is ignored.
  const [draft, setDraft] = useState<Map<number, Values>>(new Map());
  const [failure, setFailure] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const current = (row: Row) => draft.get(id(row)) ?? stored(row);
  const changes = (row: Row) => ATTRIBUTES.filter((attribute) => current(row)[attribute] !== stored(row)[attribute]);

  const all = rows.data ?? [];
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? all.filter((row) =>
        [row.display_name, row.user_principal_name].some((v) => String(v ?? '').toLowerCase().includes(needle))
      )
    : all;
  const changed = all.filter((row) => changes(row).length > 0);
  const revoking = changed.some((row) => stored(row)[ACCESS] === ACCESS_USER && current(row)[ACCESS] !== ACCESS_USER);
  const onRoster = new Set(all.map((row) => objectId(row.entra_object_id)));

  function set(row: Row, values: Values) {
    const next = new Map(draft);
    if (ATTRIBUTES.every((attribute) => values[attribute] === stored(row)[attribute])) next.delete(id(row));
    else next.set(id(row), values);
    setDraft(next);
  }

  // Taking access away takes every flag with it, in the same save. Giving it
  // back does not restore them: that is a second decision.
  function tickAccess(row: Row, on: boolean) {
    set(
      row,
      on
        ? { ...current(row), [ACCESS]: ACCESS_USER }
        : { [ACCESS]: ACCESS_NONE, [CAN_SET_HOT]: false, [CAN_MANAGE_ACCESS]: false, [CAN_USE_JOB_MENU]: false }
    );
  }

  // One request a person, one at a time: the API has no bulk update, and a
  // long list is better sent as a queue than as a burst. A failure does not
  // stop the rest, and its boxes keep their ticks so the save can be tried
  // again.
  //
  // An Access Manager sends `access` alone. Clearing it clears the flags on
  // the API's side in the same write, and the API refuses an Access Manager
  // who names a flag at all.
  async function save() {
    setSaving(true);
    setFailure(null);
    const saved: Row[] = [];
    const failed: { row: Row; error: Error }[] = [];
    for (const row of changed) {
      const values = current(row);
      const sent = changes(row).filter((attribute) => admin || attribute === ACCESS);
      try {
        await api.updateRow(SLUG, id(row), Object.fromEntries(sent.map((attribute) => [attribute, values[attribute]])));
        saved.push(row);
      } catch (caught) {
        failed.push({ row, error: caught as Error });
      }
    }
    setDraft((before) => {
      const next = new Map(before);
      for (const row of saved) next.delete(id(row));
      return next;
    });
    if (failed.length > 0) {
      setFailure(
        new Error(
          `Not saved for ${failed.map(({ row }) => name(row)).join(', ')}: ${failed
            .map(({ error }) => error.message)
            .filter((message, i, messages) => messages.indexOf(message) === i)
            .join(' ')}`
        )
      );
    }
    setSaving(false);
    if (saved.length > 0) rows.reload();
  }

  return (
    <Screen title="Admin" description="User Administration">
      <div className="admin-roster">
        <p className="muted">
          {admin
            ? 'Allow Hot lets a person mark a barrier Hot or clear it, and being an Admin does not include it. ' +
              'Can Allow Access lets them give and take away access. Job shows them the top menu: Interplant ' +
              'Transfers, Daily Production, Orders and Tables; every Admin sees it. Admin is membership of the ' +
              'admins group, and is changed in the directory.'
            : 'As an Access Manager you can give and take away access, and add people to the roster. Only an ' +
              "Admin can change Allow Hot, Can Allow Access, Job, or an Access Manager's access."}
        </p>
        <input
          type="search"
          className="admin-search"
          value={search}
          placeholder="Search by name or email"
          aria-label="Search people"
          onChange={(event) => setSearch(event.target.value)}
        />

        {admins.error && (
          <>
            <p className="admin-note">The Admin column is blank: who is an Admin could not be read.</p>
            <ErrorNote error={admins.error} />
          </>
        )}

        {rows.error ? (
          <ErrorNote error={rows.error} />
        ) : rows.loading && !rows.data ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <table className="related-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="admin-tick">Access</th>
                  <th className="admin-tick">Admin</th>
                  <th className="admin-tick">Allow Hot</th>
                  <th className="admin-tick">Can Allow Access</th>
                  <th className="admin-tick">Job</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => {
                  const values = current(row);
                  const allowed = values[ACCESS] === ACCESS_USER;
                  const isAdmin = admins.data ? admins.data.has(objectId(row.entra_object_id)) : null;
                  return (
                    <tr key={id(row)}>
                      <td>{name(row)}</td>
                      <td>{String(row.user_principal_name ?? '')}</td>
                      <td className="admin-tick">
                        <input
                          type="checkbox"
                          aria-label={`Access for ${name(row)}`}
                          checked={allowed}
                          disabled={!admin && stored(row)[CAN_MANAGE_ACCESS] === true}
                          onChange={(event) => tickAccess(row, event.target.checked)}
                        />
                      </td>
                      <td className="admin-tick">
                        {isAdmin === null ? (
                          <span className="muted" aria-label={`Admin for ${name(row)} not known`}>
                            —
                          </span>
                        ) : (
                          <input type="checkbox" aria-label={`Admin for ${name(row)}`} checked={isAdmin} disabled />
                        )}
                      </td>
                      {([
                        [CAN_SET_HOT, 'Allow Hot'],
                        [CAN_MANAGE_ACCESS, 'Can Allow Access'],
                        [CAN_USE_JOB_MENU, 'Job'],
                      ] as const).map(([attribute, label]) => (
                        <td key={attribute} className="admin-tick">
                          <input
                            type="checkbox"
                            aria-label={`${label} for ${name(row)}`}
                            checked={Boolean(values[attribute])}
                            disabled={!admin || !allowed}
                            onChange={(event) => set(row, { ...values, [attribute]: event.target.checked })}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      {needle ? `Nobody on the roster matches “${search.trim()}”.` : 'Nobody is on the roster yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {revoking && (
              <p className="admin-note">
                While the users security group is still assigned to the User app role, anyone in that group keeps
                access through it. Remove them from the group as well.
              </p>
            )}
            {failure && <ErrorNote error={failure} />}
            <div className="form-actions">
              <button
                type="button"
                className="primary"
                disabled={changed.length === 0 || saving}
                onClick={() => void save()}
              >
                Save
              </button>
              {changed.length > 0 && (
                <span className="muted">
                  {changed.length === 1 ? '1 change not saved' : `${changed.length} changes not saved`}
                </span>
              )}
            </div>

            <NotOnRoster term={search.trim()} onRoster={onRoster} onAdded={rows.reload} />
          </>
        )}
      </div>
    </Screen>
  );
}

/** The people the directory finds for the search who are not on the roster,
 *  each with Add. Nothing until the search is long enough to send. */
function NotOnRoster({ term, onRoster, onAdded }: { term: string; onRoster: Set<string>; onAdded: () => void }) {
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [searching, setSearching] = useState(false);
  const [failure, setFailure] = useState<Error | null>(null);
  // Who Add has put on the roster, hidden here at once rather than when the
  // roster has been read again, so they cannot be added twice.
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<string | null>(null);
  // Each search is numbered, and an answer that is not to the latest one is
  // dropped, as in the people picker.
  const latest = useRef(0);

  useEffect(() => {
    if (term.length < MIN_SEARCH) {
      latest.current += 1;
      setPeople([]);
      setSearching(false);
      setFailure(null);
      return;
    }
    const ticket = ++latest.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await api.searchPeople(term);
        if (ticket !== latest.current) return;
        setPeople(found);
        setFailure(null);
      } catch (caught) {
        if (ticket !== latest.current) return;
        setPeople([]);
        setFailure(caught as Error);
      } finally {
        if (ticket === latest.current) setSearching(false);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [term]);

  // The roster's default for `access` is none, so a row made with nothing but
  // who the person is gives them nothing yet.
  async function add(person: DirectoryPerson) {
    setAdding(person.object_id);
    setFailure(null);
    try {
      await api.createRow(SLUG, {
        entra_object_id: person.object_id,
        display_name: person.display_name ?? person.user_principal_name,
        user_principal_name: person.user_principal_name,
      });
      setAdded((before) => new Set(before).add(objectId(person.object_id)));
      onAdded();
    } catch (caught) {
      setFailure(caught as Error);
    } finally {
      setAdding(null);
    }
  }

  if (term.length < MIN_SEARCH) return null;
  const missing = people.filter((person) => !onRoster.has(objectId(person.object_id)) && !added.has(objectId(person.object_id)));

  return (
    <section className="admin-directory" aria-labelledby="admin-directory-label">
      <h2 id="admin-directory-label">In the directory, not on the roster</h2>
      {failure && <ErrorNote error={failure} />}
      {searching && missing.length === 0 ? (
        <p className="muted">Searching the directory…</p>
      ) : missing.length === 0 ? (
        !failure && <p className="muted">Nobody else in the directory matches “{term}”.</p>
      ) : (
        <table className="related-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th className="admin-tick" />
            </tr>
          </thead>
          <tbody>
            {missing.map((person) => {
              const label = person.display_name ?? person.user_principal_name ?? person.object_id;
              return (
                <tr key={person.object_id}>
                  <td>{label}</td>
                  <td>{person.user_principal_name ?? ''}</td>
                  <td className="admin-tick">
                    <button
                      type="button"
                      aria-label={`Add ${label}`}
                      disabled={adding !== null}
                      onClick={() => void add(person)}
                    >
                      Add
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
